import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useToast } from "@/hooks/use-toast";
import { AUTH } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const postAuthRedirect = localStorage.getItem("ams_post_auth_redirect");
      localStorage.removeItem("ams_post_auth_redirect");
      navigate(postAuthRedirect || location.state?.from?.pathname || "/dashboard");
    } catch (err) {
      toast({ variant: "destructive", title: t("auth.loginError") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <SEO title="Connexion | Kalypso Music" description="Connectez-vous à votre espace Kalypso Music pour retrouver vos chansons personnalisées." path="/login" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <img src="/logo.png" alt="Kalypso Music" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-heading text-xl font-semibold">Kalypso Music</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-8">
          <h1 className="font-heading text-2xl font-medium">{t("auth.loginTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("auth.loginSubtitle")}</p>

          <Button
            variant="outline"
            className="mt-6 w-full rounded-full"
            data-testid={AUTH.googleBtn}
            onClick={handleGoogleLogin}
            type="button"
          >
            {t("auth.googleBtn")}
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase text-muted-foreground">{t("auth.orDivider")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.emailLabel")}</Label>
              <Input id="email" type="email" autoComplete="email" required data-testid={AUTH.loginEmail} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.passwordLabel")}</Label>
              <Input id="password" type="password" required data-testid={AUTH.loginPassword} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary" data-testid={AUTH.forgotPasswordLink}>
                {t("auth.forgotPasswordLink")}
              </Link>
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid={AUTH.loginSubmit} disabled={loading}>
              {loading ? t("common.loading") : t("auth.loginBtn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="font-medium text-primary hover:underline" data-testid={AUTH.switchToRegister}>
              {t("auth.signUpLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
